import { format as formatDateFns } from "date-fns";
import { formatLocalDateYmd, parseLocalDate, toDateOnly } from "@floline/journal-aggregation";
import type { DateFormat } from "@floline/api-client";
import { i18n } from "@/i18n";
import { normalizeLanguage, type AppLanguage } from "@/i18n/languages";

const DATE_FORMAT_STORAGE_KEY = "floline-date-format";

export function getStoredDateFormat(): DateFormat | null {
  if (typeof window === "undefined") {
    return null;
  }

  const stored = window.localStorage.getItem(DATE_FORMAT_STORAGE_KEY);
  if (stored === "locale" || stored === "dmy" || stored === "mdy" || stored === "ymd") {
    return stored;
  }

  return null;
}

export function storeDateFormat(dateFormat: DateFormat): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(DATE_FORMAT_STORAGE_KEY, dateFormat);
}

function resolveAppLanguage(language?: AppLanguage): AppLanguage {
  return normalizeLanguage(language ?? i18n.resolvedLanguage ?? i18n.language);
}

function toIntlLocale(language: AppLanguage): string {
  return language === "fi" ? "fi-FI" : "en-US";
}

function dateFnsPattern(dateFormat: Exclude<DateFormat, "locale">): string {
  switch (dateFormat) {
    case "dmy":
      return "dd/MM/yyyy";
    case "mdy":
      return "MM/dd/yyyy";
    case "ymd":
      return "yyyy-MM-dd";
  }
}

function dateFnsMonthDayPattern(dateFormat: Exclude<DateFormat, "locale">): string {
  switch (dateFormat) {
    case "dmy":
      return "dd/MM";
    case "mdy":
      return "MM/dd";
    case "ymd":
      return "MM-dd";
  }
}

export function getEffectiveDateFormat(dateFormat?: DateFormat): DateFormat {
  return dateFormat ?? getStoredDateFormat() ?? "locale";
}

export function formatDateYmdDisplay(
  ymd: string,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  const language = resolveAppLanguage(options.language);
  const effectiveFormat = getEffectiveDateFormat(options.dateFormat);

  if (effectiveFormat === "locale") {
    return parseLocalDate(ymd).toLocaleDateString(toIntlLocale(language), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  return formatDateFns(parseLocalDate(ymd), dateFnsPattern(effectiveFormat));
}

export function formatDateYmdMonthDayDisplay(
  ymd: string,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  const language = resolveAppLanguage(options.language);
  const effectiveFormat = getEffectiveDateFormat(options.dateFormat);

  if (effectiveFormat === "locale") {
    return parseLocalDate(ymd).toLocaleDateString(toIntlLocale(language), {
      month: "2-digit",
      day: "2-digit",
    });
  }

  return formatDateFns(parseLocalDate(ymd), dateFnsMonthDayPattern(effectiveFormat));
}

export function formatDateInputDisplay(
  input: string,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  return formatDateYmdDisplay(toDateOnly(input), options);
}

export function formatDateDisplay(
  date: Date,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  const language = resolveAppLanguage(options.language);
  const effectiveFormat = getEffectiveDateFormat(options.dateFormat);

  if (effectiveFormat === "locale") {
    return date.toLocaleDateString(toIntlLocale(language), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  return formatDateFns(date, dateFnsPattern(effectiveFormat));
}

export function formatDateRangeYmdDisplay(
  startYmd: string,
  endYmd: string,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  if (startYmd === endYmd) {
    return formatDateYmdDisplay(startYmd, options);
  }

  return `${formatDateYmdDisplay(startYmd, options)} - ${formatDateYmdDisplay(endYmd, options)}`;
}

export function formatDateTimeIsoDisplay(
  iso: string,
  options: { dateFormat?: DateFormat; language?: AppLanguage } = {}
): string {
  const language = resolveAppLanguage(options.language);
  const locale = toIntlLocale(language);
  const effectiveFormat = getEffectiveDateFormat(options.dateFormat);
  const date = new Date(iso);

  const datePart =
    effectiveFormat === "locale"
      ? date.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" })
      : formatDateFns(date, dateFnsPattern(effectiveFormat));

  const timePart = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${datePart} ${timePart}`;
}

export function formatTodayYmd(): string {
  return formatLocalDateYmd(new Date());
}
